import { DateField } from "components/DesignSystem/DateInput/DateField";
import { Button } from "components/DesignSystem/Button/Button";
import Modal from "components/DesignSystem/Modal/Modal";
import { FormikForm } from "components/FormikForm/FormikForm";
import { CalendarIcon } from "components/icons/CalendarIcon";
import { EditPencil } from "components/icons/EditPencil";
import { TriangleInfo } from "components/icons/TriangleInfo";
import { YYYY_MM_DD } from "constants/date";
import dayjs from "dayjs";
import { Form, FormikValues } from "formik";
import { useModal } from "hooks/useModal";
import { useRoleBasedView } from "hooks/useRoleBasedView";
import { useToast } from "hooks/useToast";
import {
  useUpdateTaskAssigneeAdminMutation,
  useUpdateTaskAssigneeProviderMutation,
} from "store/apis/serviceTeam";
import { Task } from "types/Models/task";
import { BackendError } from "types/utils/error";
import { object, string } from "yup";

type TaskDeadlineProps = {
  rowData: Task;
};

export const UpdateTaskDeadline = ({ rowData }: TaskDeadlineProps) => {
  const { alertToast, successToast } = useToast();
  const deadlineUpdateModal = useModal();
  const [updateTaskDeliveryTimeAdmin, { isLoading: isAdminUpdating }] =
    useUpdateTaskAssigneeAdminMutation();
  const [updateTaskDeliveryTime, { isLoading: isUpdating }] =
    useUpdateTaskAssigneeProviderMutation();
  const { isAdmin } = useRoleBasedView();
  const confirmUpdateModal = useModal();

  let updateTaskAssignee = isAdmin
    ? updateTaskDeliveryTimeAdmin
    : updateTaskDeliveryTime;

  const handleSubmit = async (values: FormikValues) => {
    try {
      if (!values.deadline) {
        alertToast({ message: "Please select a valid date" });
        return;
      }
      await updateTaskAssignee({
        taskId: rowData.uuid,
        deadline_date: dayjs(values.deadline).format(YYYY_MM_DD),
      }).unwrap();
      successToast({ message: "Deadline updated successfully" });
      deadlineUpdateModal.close();
      confirmUpdateModal.close();
    } catch (error) {
      alertToast({
        message: (error as BackendError)?.data?.error?.message,
      });
    }
  };

  const isLoading = isAdminUpdating || isUpdating;

  return (
    <Modal.Root
      onOpenChange={deadlineUpdateModal.toggle}
      open={deadlineUpdateModal.isOpen}
    >
      <Modal.Trigger asChild>
        <Button customType="ghost" size="small">
          <EditPencil className="t-text-text-30" />
        </Button>
      </Modal.Trigger>
      <Modal.Content asChild>
        <FormikForm
          initialValues={{ deadline: rowData.deadline }}
          validationSchema={() =>
            object({
              deadline: string().required("Deadline is required"),
            })
          }
          onSubmit={handleSubmit}
        >
          {({ setFieldValue, submitForm }) => (
            <>
              <Modal.Header>
                <Modal.Title>Edit Task Deadline</Modal.Title>
                <Modal.Close />
              </Modal.Header>
              <Modal.Body>
                <DateField
                  name="deadline"
                  dateProps={{
                    label: "Deadline",
                    required: true,
                    onDateChange: (date: Date) =>
                      setFieldValue("deadline", dayjs(date).format(YYYY_MM_DD)),
                    rightComponent: (
                      <CalendarIcon className="t-text-text-30 -t-mt-0.5" />
                    ),
                  }}
                />
              </Modal.Body>
              <Modal.FooterButtonGroup>
                <Modal.RawClose asChild>
                  <Button type="button">Cancel</Button>
                </Modal.RawClose>
                <Modal.Root
                  onOpenChange={confirmUpdateModal.toggle}
                  open={confirmUpdateModal.isOpen}
                >
                  <Modal.Trigger asChild>
                    <span>
                      <Button type="button" customType="primary">
                        Save
                      </Button>
                    </span>
                  </Modal.Trigger>
                  <Modal.Content>
                    <Modal.Body>
                      <TriangleInfo />
                      <div className="t-text-subtext t-text-text-100 t-mt-5 t-mb-2">
                        Update Filing Deadline?
                      </div>
                      <div className="t-text-body-sm t-text-text-30">
                        Updating this filing’s deadline will update connected
                        records, deadlines, and compliance status.
                      </div>
                    </Modal.Body>

                    <Modal.FooterButtonGroup>
                      <Modal.RawClose asChild>
                        <Button type="button">Cancel</Button>
                      </Modal.RawClose>
                      <Button
                        customType="primary"
                        type="submit"
                        isLoading={isLoading}
                        disabled={isLoading}
                        onClick={submitForm}
                      >
                        Yes update
                      </Button>
                    </Modal.FooterButtonGroup>
                  </Modal.Content>
                </Modal.Root>
              </Modal.FooterButtonGroup>
            </>
          )}
        </FormikForm>
      </Modal.Content>
    </Modal.Root>
  );
};
